"use client"

import { useQuery } from "@tanstack/react-query"

import { ensureFileToken, heldFileToken, tenantPb } from "@/lib/pb/clients"
import { useAuthStore } from "@/stores/auth-store"

type FileRecord = { id: string; collectionId?: string; collectionName?: string }

/**
 * The address of a protected photo — a customer's picture or a team member's
 * face — with the token that opens it. The token is the one the tenant client
 * holds; see clients.ts for how it is kept current.
 */
export function protectedFileUrl(
  record: FileRecord,
  filename: string,
  options: { thumb?: string } = {},
) {
  const token = heldFileToken()
  return tenantPb.files.getURL(record as never, filename, token ? { ...options, token } : options)
}

/**
 * The current file token, for a component that builds a photo's address as it
 * draws rather than when its data is fetched. Asked again well before it runs
 * out, which redraws the component with a fresh address.
 */
export function useFileToken() {
  const tenant = useAuthStore((state) => state.tenant?.id ?? null)
  const { data } = useQuery({
    queryKey: ["file-token", tenant],
    queryFn: async () => {
      await ensureFileToken()
      return heldFileToken()
    },
    staleTime: 20 * 60 * 1000,
    refetchInterval: 20 * 60 * 1000,
  })
  return data ?? ""
}
